import { useQuery } from "@tanstack/react-query";
import { useGooglePlaces } from "../location/useGooglePlaces";

type OptimisticLocation = {
    lat: number;
    lng: number;
    label: string;
};

type UseLocationLabelOptions = {
    lat?: number | null;
    lng?: number | null;
    fallbackLabel?: string;
    optimisticLocation?: OptimisticLocation | null;
};

const LOCATION_LABEL_STALE_TIME = 1000 * 60 * 60 * 24;

export const getLocationLabelQueryKey = (lat: number, lng: number) =>
    ["location-label", lat, lng] as const;

export function useLocationLabel({
    lat,
    lng,
    fallbackLabel = "Near you",
    optimisticLocation = null,
}: UseLocationLabelOptions) {
    const { reverseGeocode } = useGooglePlaces();
    const hasLocation = lat != null && lng != null;
    const optimisticLabel =
        optimisticLocation != null &&
        hasLocation &&
        optimisticLocation.lat === lat &&
        optimisticLocation.lng === lng
            ? optimisticLocation.label.trim()
            : "";

    const { data: resolvedLocationLabel = "" } = useQuery({
        queryKey: hasLocation
            ? getLocationLabelQueryKey(lat, lng)
            : ["location-label", null, null],
        enabled: hasLocation,
        queryFn: async () => {
            const result = await reverseGeocode(lat as number, lng as number);
            return result?.displayName?.trim() ?? "";
        },
        staleTime: LOCATION_LABEL_STALE_TIME,
        placeholderData: optimisticLabel || undefined,
    });

    const locationLabel = resolvedLocationLabel || optimisticLabel;
    const locationValue =
        locationLabel || (hasLocation ? fallbackLabel : undefined);

    return { hasLocation, locationLabel, locationValue };
}
